import React, { useCallback, useEffect, useRef } from "react";
import { Editor } from "@toast-ui/react-editor";
import "@toast-ui/editor/dist/toastui-editor.css";
import "./style.css";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
type CustomToolbarItem = {
  name: string;
  command: string;
  tooltip: string;
  className?: string;
  handler: (editor: any) => void;
};

interface Props {
  value: string;
  disabled?: boolean;
  placeholder?: string;
  onBlur?: (markdown: string) => void;
  onChange?: (markdown: string) => void;
  onDebouncedChange?: (markdown: string) => void;
  debounceMs?: number;
  customToolbarItems?: CustomToolbarItem[];
  className?: string;
  borderColor?: string;
}

const toolbarItems = [
  ["heading", "bold", "italic"],
  ["ul", "ol", "indent", "outdent"],
  ["table", "link"],
];

const DEFAULT_HEIGHT = 132;

const MarkdownTextArea: React.FC<Props> = ({
  value,
  disabled,
  placeholder,
  onBlur,
  onChange,
  onDebouncedChange,
  debounceMs = 3000,
  customToolbarItems = [],
  className = "",
  borderColor,
}) => {
  const editorRef = useRef<Editor>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const lastHeightRef = useRef<number>(DEFAULT_HEIGHT);
  const didInit = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestValueRef = useRef(value);

  const flushDebouncedSave = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (onDebouncedChange) {
      const markdown = editorRef.current?.getInstance()?.getMarkdown() || "";
      if (markdown !== latestValueRef.current) {
        onDebouncedChange(markdown);
        latestValueRef.current = markdown;
      }
    }
  }, [onDebouncedChange]);

  // Flush pending save on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        flushDebouncedSave();
      }
    };
  }, [flushDebouncedSave]);

  useEffect(() => {
    latestValueRef.current = value;
    if (!didInit.current) return;
    editorRef.current?.getInstance().setMarkdown(value || "");
  }, [value]);

  useEffect(() => {
    if (didInit.current && editorRef.current) {
      const instance = editorRef?.current?.getInstance();
      customToolbarItems.forEach((btn) => {
        instance?.addCommand("wysiwyg", btn.command, () => {
          btn.handler(instance);
          return true;
        });
      });
    }
  }, [didInit.current]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    wrapper.style.height = `${DEFAULT_HEIGHT}px`;
    lastHeightRef.current = DEFAULT_HEIGHT;

    // Custom styling logic to force outer wrapper of editor to be the same height as inner div that is resizable
    const wwContainer = wrapper.querySelector(
      ".toastui-editor-ww-container"
    ) as HTMLElement | null;

    if (!wwContainer) return;

    const observer = new ResizeObserver(() => {
      const rect = wwContainer.getBoundingClientRect();
      const contentHeight = Math.round(rect.height);

      if (contentHeight <= 0) return;

      const nextHeight = contentHeight + 32;

      if (Math.abs(nextHeight - lastHeightRef.current) < 1) return;

      lastHeightRef.current = nextHeight;
      wrapper.style.height = `${nextHeight}px`;
    });

    observer.observe(wwContainer);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    //override native autofocus/scroll behaviors
    let lastScrollY = window.scrollY;

    const onScroll = () => {
      lastScrollY = window.scrollY;
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    const observer = new MutationObserver(() => {
      // restore scroll after any DOM/layout mutation
      requestAnimationFrame(() => {
        window.scrollTo({ top: lastScrollY, behavior: "auto" });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      window.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, []);

  // Override Tab to move focus to next/previous field instead of indenting
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      e.preventDefault();
      e.stopPropagation();

      const focusable = Array.from(
        document.querySelectorAll<HTMLElement>(
          '[contenteditable="true"], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);

      const current = focusable.findIndex((el) => wrapper.contains(el));
      if (current === -1) return;

      const next = e.shiftKey
        ? focusable[current - 1]
        : focusable[current + 1];
      next?.focus();
    };

    wrapper.addEventListener("keydown", handleKeyDown, true);
    return () => wrapper.removeEventListener("keydown", handleKeyDown, true);
  }, []);

  //override border color
  useEffect(() => {
    if (borderColor) {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;

      const defaultUI = wrapper.querySelector(
        ".toastui-editor-defaultUI"
      ) as HTMLElement | null;

      if (!defaultUI) return;
      defaultUI.style.border = `1px solid ${borderColor || "#ced4da"}`;
    }
  }, [borderColor]);

  return disabled ? (
    <div
      className={`markdownTextArea ${className} disabled`}
      style={{ borderColor }}
    >
      <ReactMarkdown>{value}</ReactMarkdown>
    </div>
  ) : (
    <div ref={wrapperRef} className={`markdownTextArea ${className}`}>
      {/* @ts-ignore */}
      <Editor
        ref={editorRef}
        initialValue={value || ""}
        initialEditType="wysiwyg"
        hideModeSwitch
        usageStatistics={false}
        toolbarItems={[...toolbarItems, customToolbarItems]}
        height="100%"
        placeholder={placeholder}
        onBlur={() => {
          if (onDebouncedChange) {
            flushDebouncedSave();
          } else if (onBlur) {
            const markdown = editorRef.current?.getInstance()?.getMarkdown();
            if (markdown && markdown !== value) onBlur(markdown);
          }
        }}
        onChange={() => {
          const markdown = editorRef.current?.getInstance()?.getMarkdown();
          if (onChange) {
            if (markdown && markdown !== value) onChange(markdown);
          }
          if (onDebouncedChange && markdown && markdown !== latestValueRef.current) {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = setTimeout(() => {
              debounceTimerRef.current = null;
              onDebouncedChange(markdown);
              latestValueRef.current = markdown;
            }, debounceMs);
          }
        }}
        onLoad={() => {
          didInit.current = true;
        }}
        //@ts-ignore
        readOnly={disabled}
        autofocus={false}
      />
    </div>
  );
};

export default MarkdownTextArea;
